import { parseDateString } from '@co-3/ctrl-ui/common';
import { format } from 'date-fns';
import { Contract } from './contract';
import { ValidatorFn } from '@angular/forms';

export enum DamageReportState {
    REPORTED = 3,
    IN_PROGRESS = 4,
    COMPLETED = 5,
}

const isFullDamageReport = (x: unknown): x is DamageReport =>
    typeof x === 'object' &&
    'id' in x &&
    typeof x.id === 'number' &&
    'state' in x &&
    typeof x.state === 'number' &&
    x.state in DamageReportState &&
    'stateDescription' in x &&
    typeof x.stateDescription === 'string';

export interface DamageReport {
    readonly id: number;
    contractId: number;
    createdAt: Date;
    damageDate: Date;
    location: string;
    eventDescription: string;
    damageDescription: string;
    notes: string;
    state: DamageReportState;
    stateDescription: string;
}

export interface DamageReportDto {
    Id: number;
    ContractId: number;
    EntryDate: string;
    DamageDate: string;
    Location: string;
    DescriptionEvent: string;
    DescriptionDamage: string;
    Notes: string;
    DamageState: {
        Id: number;
        Description: string;
    };
}

export type CreateDamageReport = Omit<DamageReport, 'id' | 'state' | 'stateDescription'>;
export type CreateDamageReportDto = Omit<DamageReportDto, 'Id' | 'DamageState'>;

export const damageReportFromDto = (dto: DamageReportDto): DamageReport => ({
    id: dto.Id,
    contractId: dto.ContractId,
    createdAt: parseDateString(dto.EntryDate, 'dd.MM.yyyy'),
    damageDate: parseDateString(dto.DamageDate, 'dd.MM.yyyy'),
    location: dto.Location,
    eventDescription: dto.DescriptionEvent,
    damageDescription: dto.DescriptionDamage,
    notes: dto.Notes,
    state: dto.DamageState.Id as DamageReportState,
    stateDescription: dto.DamageState.Description,
});

export function damageReportToDto(damageReport: DamageReport): DamageReportDto;
export function damageReportToDto(damageReport: CreateDamageReport): CreateDamageReportDto;
export function damageReportToDto(
    damageReport: DamageReport | CreateDamageReport,
): DamageReportDto | CreateDamageReportDto {
    const dto: Omit<DamageReportDto, 'Id' | 'DamageState'> = {
        ContractId: damageReport.contractId,
        EntryDate: format(damageReport.createdAt, 'dd.MM.yyyy'),
        DamageDate: format(damageReport.damageDate, 'dd.MM.yyyy'),
        Location: damageReport.location,
        DescriptionEvent: damageReport.eventDescription,
        DescriptionDamage: damageReport.damageDescription,
        Notes: damageReport.notes,
    };

    if (isFullDamageReport(damageReport)) {
        return {
            Id: damageReport.id,
            DamageState: {
                Id: damageReport.state,
                Description: damageReport.stateDescription,
            },
            ...dto,
        };
    }
    return dto;
}

export interface DamageReportFileAttachment {
    readonly id: number;
    damageId: number;
    documentTypeId: number;
    url: string;
    description: string;
}

export interface DamageReportFileAttachmentDto {
    Id: number;
    DamageId: number;
    DocumentTypeId: number;
    Filename: string;
    Description: string;
}

export const damageReportFileAttachmentFromDto = (dto: DamageReportFileAttachmentDto): DamageReportFileAttachment => ({
    id: dto.Id,
    damageId: dto.DamageId,
    documentTypeId: dto.DocumentTypeId,
    url: dto.Filename,
    description: dto.Description,
});

export type WithFileAttachments<T extends DamageReport> = T & { attachments: DamageReportFileAttachment[] };

export interface DamageDocumentType {
    id: number;
    description: string;
}

export interface DocumentTypeDto {
    Id: number;
    Type: string;
}

export const documentTypeFromDto = (dto: DocumentTypeDto): DamageDocumentType => ({
    id: dto.Id,
    description: dto.Type,
});

export type DamageReportFormElements = Pick<
    DamageReport,
    'damageDate' | 'location' | 'eventDescription' | 'damageDescription' | 'notes'
>;

export type DamageReportValidatorConfig = {
    [key in keyof DamageReportFormElements]: ValidatorFn[] | ((contract: Contract) => ValidatorFn[]);
};
